import { Directive, ElementRef, inject, output } from '@angular/core';

export interface IScrollStateScrollInvertedDirective {
    scrollTop: number;
    scrollHeight: number;
    clientHeight: number;
}

@Directive({
    selector: '[scrollInverted]',
})
export class ScrollInvertedDirective {
    private readonly elementRef = inject(ElementRef<HTMLElement>);

    public readonly scrolledToTop = output<void>();
    public readonly scrollStateChange = output<IScrollStateScrollInvertedDirective>();

    private previousScrollHeight = 0;
    private previousScrollTop = 0;
    private isLoadingMore = false;

    constructor() {
        this.setupScrollListener();
    }

    private setupScrollListener(): void {
        const element = this.elementRef.nativeElement;

        element.addEventListener('scroll', () => {
            const state: IScrollStateScrollInvertedDirective = {
                scrollTop: element.scrollTop,
                scrollHeight: element.scrollHeight,
                clientHeight: element.clientHeight,
            };

            this.scrollStateChange.emit(state);

            if (element.scrollTop < 10 && !this.isLoadingMore) {
                this.onScrollToTop();
            }
        });
    }

    private onScrollToTop(): void {
        this.isLoadingMore = true;
        const element = this.elementRef.nativeElement;

        this.previousScrollHeight = element.scrollHeight;
        this.previousScrollTop = element.scrollTop;

        this.scrolledToTop.emit();
    }

    public restoreScrollPosition(): void {
        requestAnimationFrame(() => {
            const element = this.elementRef.nativeElement;
            const newScrollHeight = element.scrollHeight;

            const heightDifference = newScrollHeight - this.previousScrollHeight;
            const newScrollTop = this.previousScrollTop + heightDifference;

            element.scrollTo({
                top: newScrollTop,
                behavior: 'instant' as ScrollBehavior,
            });

            this.isLoadingMore = false;
        });
    }

    public scrollToBottom(behavior: ScrollBehavior = 'smooth'): void {
        const element = this.elementRef.nativeElement;

        if (behavior === 'instant') {
            requestAnimationFrame(() => {
                element.scrollTo({
                    top: element.scrollHeight,
                    behavior: 'instant' as ScrollBehavior,
                });
            });
        } else {
            element.scrollTo({
                top: element.scrollHeight,
                behavior,
            });
        }
    }

    public isNearBottom(threshold: number = 30): boolean {
        const element = this.elementRef.nativeElement;
        const diff = Math.abs(element.scrollTop + element.clientHeight - element.scrollHeight);
        return diff < threshold;
    }

    public resetLoadingState(): void {
        this.isLoadingMore = false;
    }
}
